/**
 * Cmd-K palette query parsing + filtering (#134).
 *
 * The palette accepts a free-text query optionally prefixed by one or
 * more `key:value` operators (per `20-roadmap.md §11.IV`):
 *
 *   - `type:<root>`     restrict results to one entity root. The value
 *                       may be the full root name (`character`) or any
 *                       unambiguous prefix (`char`).
 *   - `is:<predicate>`  restrict results to entities that carry that
 *                       boolean predicate as a fact. Negation is
 *                       expressed as `is:!<predicate>` (one extra
 *                       line — multi-clause boolean parsing is Phase 2).
 *
 * Unknown operator keys are NOT errors; the token is treated as part
 * of the literal query text, so a typo like `type:caracter` surfaces
 * as a literal substring match rather than crashing the palette.
 *
 * Why parse here instead of in the search router? The palette renders
 * synchronously against the in-memory `model.entities` snapshot (see
 * `viewPalette` rationale in `palette/view.ts`); it has no live handle
 * to the replica. The operator vocabulary mirrors what would arrive
 * via `SearchScope.roots` (#39) + a future predicate-presence lane —
 * when the palette migrates to the async search router, this parser
 * becomes the producer of `SearchScope` instead of the in-memory
 * filter.
 */
import {
  type EntityRoot,
  type EntityState,
} from '@dl/shared';

import { ALL_ENTITY_ROOTS } from '../entity-roots.js';

/**
 * The shape produced by `parsePaletteQuery`. The `literal` field is
 * the part of the query that survives operator stripping — it's what
 * the name-lane scoring pipeline matches against.
 */
export interface ParsedPaletteQuery {
  /** The free-text portion of the query, with operators removed. */
  readonly literal: string;
  /** When set, restrict results to this entity root (operator `type:`). */
  readonly rootFilter: EntityRoot | null;
  /**
   * When set, restrict results to entities that carry this predicate
   * as a fact (operator `is:`). The optional `negate: true` filters
   * to entities that DO NOT carry that predicate.
   */
  readonly predicateFilter: { predicate: string; negate: boolean } | null;
  /**
   * The raw operator tokens that were recognised + stripped, in the
   * order they were parsed. Drives the operator-chip UI; the i18n
   * key is `palette.filter_chip_type` / `palette.filter_chip_is`.
   */
  readonly recognisedOperators: readonly RecognisedOperator[];
}

export type RecognisedOperator =
  | { readonly kind: 'type'; readonly root: EntityRoot }
  | { readonly kind: 'is'; readonly predicate: string; readonly negate: boolean };

/**
 * The unambiguous short-form aliases for each entity root. Any
 * `type:` value that matches an alias resolves to its canonical root.
 * The mapping is deliberately conservative — `char` → `character`,
 * `loc` → `location` — to keep the parser predictable.
 */
const ROOT_ALIASES: Record<string, EntityRoot> = {
  character: 'character',
  char: 'character',
  location: 'location',
  loc: 'location',
  faction: 'faction',
  fac: 'faction',
  event: 'event',
  evt: 'event',
  item: 'item',
  lore: 'lore',
  lor: 'lore',
  // `concept`/`con` retained as back-compat aliases — the root was renamed
  // from `concept` to `lore`, but the old terms stay searchable.
  concept: 'lore',
  con: 'lore',
};

function resolveRootAlias(value: string): EntityRoot | null {
  const normalised = value.trim().toLowerCase();
  if (normalised.length === 0) return null;
  // Direct alias hit.
  const direct = ROOT_ALIASES[normalised];
  if (direct) return direct;
  // Otherwise: any single root whose name *starts with* the value is
  // accepted. `item` and `event` are unambiguous prefixes already; this
  // catches future shorthands without growing the alias table.
  const matches = ALL_ENTITY_ROOTS.filter((r) => r.startsWith(normalised));
  if (matches.length === 1) return matches[0]!;
  return null;
}

/**
 * Parse a palette query into operator filters + literal text.
 *
 * The parser walks tokens left-to-right; on the first token that is
 * NOT a recognised `key:value` operator it stops and treats the
 * remainder of the input (verbatim, whitespace preserved) as the
 * literal query. This keeps the UX predictable — a user typing
 * `type:character harbour` searches characters named "harbour", but
 * `harbour type:character` searches for the literal text
 * "harbour type:character" (no surprise reordering).
 */
export function parsePaletteQuery(input: string): ParsedPaletteQuery {
  const operators: RecognisedOperator[] = [];
  let rootFilter: EntityRoot | null = null;
  let predicateFilter: ParsedPaletteQuery['predicateFilter'] = null;

  let rest = input;
  // Strip a leading run of `key:value` tokens. A token ends at the
  // first whitespace; we re-slice the original string so the literal
  // tail preserves the user's exact spacing.
  // Loop bound: the rest shrinks every iteration (we slice past the
  // consumed token + its trailing whitespace) or we break.
  for (;;) {
    const leading = rest.match(/^(\s*)(\S+)(\s*)/);
    if (!leading) break;
    const [whole, , token = '', trailing = ''] = leading;
    if (!whole) break;
    const colon = token.indexOf(':');
    if (colon <= 0 || colon === token.length - 1) {
      // Not a key:value token — leave it for the literal pass.
      break;
    }
    const key = token.slice(0, colon).toLowerCase();
    const value = token.slice(colon + 1);
    let consumed = false;
    if (key === 'type' && rootFilter === null) {
      const resolved = resolveRootAlias(value);
      if (resolved !== null) {
        rootFilter = resolved;
        operators.push({ kind: 'type', root: resolved });
        consumed = true;
      }
    } else if (key === 'is' && predicateFilter === null) {
      // `is:!visited` negates; `is:visited` is plain.
      const negate = value.startsWith('!');
      const predicate = negate ? value.slice(1) : value;
      if (isPredicateName(predicate)) {
        predicateFilter = { predicate, negate };
        operators.push({ kind: 'is', predicate, negate });
        consumed = true;
      }
    }
    if (!consumed) break;
    rest = rest.slice(whole.length);
    // Preserve trailing whitespace only if there is more content; we
    // recompute by re-slicing the source to keep the literal intact.
    // (The whole match already consumed the trailing whitespace; this
    // is what we want so the literal starts at the next non-ws char.)
    void trailing;
  }

  return {
    literal: rest,
    rootFilter,
    predicateFilter,
    recognisedOperators: operators,
  };
}

/**
 * A predicate name is `lower_snake_case` per the PredicateRegistry
 * (#52 / `03-information-model.md §5`). We mirror the same regex the
 * search graph lane uses (`graph-lane.ts:139`) so the palette never
 * accepts an `is:` value that the substrate would reject downstream.
 */
function isPredicateName(s: string): boolean {
  return /^[a-z_][a-z0-9_]*$/.test(s);
}

/**
 * Apply the parsed filters + name-lane scoring against the in-memory
 * entity snapshot. The output shape matches what the SearchRouter
 * name-lane returns at merge time (id + name + root), capped at 50.
 *
 * Scoring mirrors `paletteMatches`' pre-#134 implementation (exact >
 * prefix > substring) so the visible ordering doesn't shift when a
 * query has no operators.
 */
export interface PaletteResult {
  readonly id: string;
  readonly name: string;
  readonly root: EntityRoot;
}

export function filterEntities(
  parsed: ParsedPaletteQuery,
  entities: Iterable<EntityState>,
  options: {
    readonly normalize: (s: string) => string;
    readonly factName: (entity: EntityState) => string;
    readonly limit?: number;
  },
): PaletteResult[] {
  const limit = options.limit ?? 50;
  const needle = options.normalize(parsed.literal);

  const filtered: { entity: EntityState; name: string }[] = [];
  for (const e of entities) {
    if (e.soft_deleted) continue;
    if (parsed.rootFilter !== null && e.root !== parsed.rootFilter) continue;
    if (parsed.predicateFilter !== null) {
      const has = e.facts[parsed.predicateFilter.predicate] !== undefined;
      if (parsed.predicateFilter.negate ? has : !has) continue;
    }
    filtered.push({ entity: e, name: options.factName(e) });
  }

  // Empty literal → return all matching entities (still capped). This
  // keeps `type:character` (no text) usable as a "show all characters"
  // filter — the docs treat operators as legitimate standalone queries.
  if (needle.length === 0) {
    return filtered
      .slice(0, limit)
      .map(({ entity, name }) => ({ id: entity.id, name, root: entity.root }));
  }

  const scored: { id: string; name: string; root: EntityRoot; score: number }[] = [];
  for (const { entity, name } of filtered) {
    const norm = options.normalize(name);
    let score: number;
    if (norm === needle) score = 3;
    else if (norm.startsWith(needle)) score = 2;
    else if (norm.includes(needle)) score = 1;
    else continue;
    scored.push({ id: entity.id, name, root: entity.root, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored
    .slice(0, limit)
    .map(({ id, name, root }) => ({ id, name, root }));
}

/**
 * Does the parsed query exactly match an existing entity name (under
 * the same normaliser the name-lane uses)? When true, the quick-create
 * rows are suppressed — typing an existing entity's name should
 * surface that entity, not offer to create six duplicates.
 */
export function hasExactMatch(
  parsed: ParsedPaletteQuery,
  entities: Iterable<EntityState>,
  options: {
    readonly normalize: (s: string) => string;
    readonly factName: (entity: EntityState) => string;
  },
): boolean {
  const needle = options.normalize(parsed.literal);
  if (needle.length === 0) return false;
  for (const e of entities) {
    if (e.soft_deleted) continue;
    if (parsed.rootFilter !== null && e.root !== parsed.rootFilter) continue;
    if (options.normalize(options.factName(e)) === needle) return true;
  }
  return false;
}

/**
 * The roots for which quick-create rows should render under the
 * current operator filter. With no `type:` operator → all six. With
 * `type:character` → only `character`. Drives the "create-for-root"
 * loop in `view.ts`.
 */
export function quickCreateRoots(
  parsed: ParsedPaletteQuery,
): readonly EntityRoot[] {
  if (parsed.rootFilter !== null) return [parsed.rootFilter];
  return ALL_ENTITY_ROOTS;
}
